import type { ReactNode } from 'react';

import { push } from 'connected-react-router';
import { Formik } from 'formik';
import qs from 'query-string';
import { useHistory } from 'react-router';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import GenericError from 'src/app/components/Error/Generic';
import { SegmentFormIncomplete } from 'src/app/components/Error/SegmentFormIncomplete';
import { PasswordInput } from 'src/app/components/Form/TextInput/Password';
import { useUserCheckPassword } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { extractValidationErrors } from 'src/app/popsicle/utils';
import { logout as logoutPath, notFound } from 'src/app/router/paths';
import { useDispatch } from 'src/app/state/store';

import { Container, Form, Header, SubmitButton } from '../styles';

import {
  AttemptingToUseExpiredLink,
  AttemptingToUsePreviouslyUsedPasswordError,
  GenericPasswordError,
  getIsInvalidTokenError,
  RateLimitedError,
} from './utils';
import validation from './validation';

export interface ChangePasswordUnwiredProps {
  isSubmitting: boolean;
  status: string;
  customErrorMarkup: string;
  setCustomErrorMarkup: (arg: ReactNode) => void;
  dispatchSegmentTrack: (event: UnifiedEvent, payload: any) => any;
}

export const ChangePassword = ({
  isSubmitting,
  status,
  customErrorMarkup,
  setCustomErrorMarkup,
  dispatchSegmentTrack,
}: ChangePasswordUnwiredProps) => {
  const dispatch = useDispatch();
  useSegmentEvent('viewResetPassword', { shouldTrackOnMount: true });

  const { forgotPassword, forgotPasswordSubmit, isPrevPassword: isPrevPasswordAsync } = useAuth();
  const { mutateAsync: checkPassword } = useUserCheckPassword();

  const { replace } = useHistory();

  return (
    <Container>
      <Header>Choose your new password</Header>
      <Formik
        initialValues={validation.initialValues}
        validationSchema={validation.schema}
        validateOnChange={true}
        validateOnBlur={false}
        onSubmit={async (values, { setErrors, setSubmitting, setFieldError }) => {
          //const { setCustomErrorMarkup, redirectTo404, redirectToLogin, dispatchSegmentTrack, code } = props;
          const { password } = values;

          setCustomErrorMarkup(null);
          setSubmitting(true);

          // We want to store email/cognito UUID for usage in some errors.
          let userIdentifier = '';

          try {
            // New way --> /:encodedUserId/:token?confirmationCode=#&userUuid=#
            const confirmationCode = qs.parse(window.location.search)?.confirmationCode as string | undefined;
            const userUuid = qs.parse(window.location.search)?.userUuid as string | undefined;

            if ((confirmationCode || userUuid) && !(confirmationCode && userUuid)) {
              await dispatch(push(notFound.route));
              return;
            }

            if (confirmationCode && userUuid) {
              const isPreviousPassword = await isPrevPasswordAsync(userUuid, password);
              if (isPreviousPassword) {
                setCustomErrorMarkup(AttemptingToUsePreviouslyUsedPasswordError);
                setSubmitting(false);
                return;
              }

              const passwordCheckResp = await checkPassword(password);
              if (!passwordCheckResp.isSafe) {
                setFieldError(
                  'password',
                  'This password is not secure or is too common. Try increasing length or adding numbers and symbols.'
                );
                setSubmitting(false);
                return;
              }

              userIdentifier = userUuid;
              await forgotPasswordSubmit(userUuid, confirmationCode, password);

              dispatchSegmentTrack(UNIFIED.resetPasswordSuccessful, {});
              return replace(logoutPath.route, { isGlobal: 'true', message: 'Your password has been reset.' });
            }
          } catch (error: any) {
            // link has expired
            if (getIsInvalidTokenError(error)) {
              // send a new link for them

              forgotPassword(userIdentifier)
                .then(() => {
                  setCustomErrorMarkup(AttemptingToUseExpiredLink);
                })
                .catch(() => {
                  // if our automatic password reset fails, something weird is happening.
                  setCustomErrorMarkup(GenericPasswordError);
                });
            }

            // rate limit occurring
            if (
              error?.code === 'TooManyFailedAttemptsException' ||
              error?.code === 'TooManyRequestsException' ||
              error?.message === 'Password attempts exceeded'
            ) {
              setCustomErrorMarkup(RateLimitedError);
            }

            // other errors
            else if (error.kind || error.details) {
              setCustomErrorMarkup(GenericPasswordError);
            }

            dispatchSegmentTrack(UNIFIED.resetPasswordFailed, {});
            setErrors(extractValidationErrors(error));
            setSubmitting(false);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <Form>
          <GenericError customErrorMarkup={customErrorMarkup} />
          <SegmentFormIncomplete />
          <PasswordInput label="New password" autoComplete="new-password" />

          <SubmitButton isSubmitting={isSubmitting}>Update Password</SubmitButton>

          {status && <div color="success">{status}</div>}
        </Form>
      </Formik>
    </Container>
  );
};
